/** @typedef {import('headlight-configurable-application').Pict} Pict */
const PictProvider = require('pict-provider');
const EntityDataBaseline = require('headlight-dynamic-inputs-bundle/source/providers/_EntityDataBaseline.js');

// Bounds one drain of the save queue. Reachable only by a save trigger that fires from inside the save
// lifecycle itself (an onAfterDocumentSave override, say), which would otherwise re-queue forever.
const MAX_CONSECUTIVE_SAVE_CYCLES = 25;

// Busy-toast phrasing keyed on the lowercased approval action. The action list is server-defined
// (fetchApprovalState), so anything unrecognized falls back to naming the action verbatim.
const APPROVAL_BUSY_PHRASE =
{
	'submit': 'Submitting this document for approval.',
	'approve': 'Approving this document.',
	'reject': 'Rejecting this document.',
	'draft': 'Returning this document to draft.',
};
const APPROVAL_BUSY_SUFFIX = ' This can take a few moments; the page will refresh when it finishes.';
// How long a silent server gets before the busy toast escalates its wording. Never releases the lock.
const APPROVAL_BUSY_ESCALATION_MS = 30000;

class DocumentProvider extends PictProvider
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {import('fable') & Pict} */
		this.fable;
		/** @type {Pict} */
		this.pict;
		/** @type {any} */
		this.options;
		/** @type {any} */
		this.AppData;
		/** @type {any} */
		this.log;
		/** @type {any} */
		this.services;

		this.pict.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));

		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.instantiateServiceProvider('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });

		/** @type {boolean} - Whether a save lifecycle is currently running. */
		this.IsSaving = false;
		/** @type {boolean} - Whether a save was requested while one was already running. */
		this._SavePending = false;
		/** @type {boolean} - Mute setting for the queued trailing save; any un-muted requester un-mutes it. */
		this._PendingMuteNotification = true;
		/** @type {Array<(pError?: Error) => void>} - Resolvers waiting on the trailing save that will carry their mutation. */
		this._PendingSaveWaiters = [];
		/** @type {Array<() => void>} - Resolvers waiting for the current save drain to finish, whatever its outcome. */
		this._SaveSettledWaiters = [];
		/** @type {boolean} - Whether an approval action (submit/approve/reject) is currently running. */
		this.isTakingAction = false;
		/** @type {?{ dismiss: function }} - Handle for the persistent toast shown while an action runs. */
		this._ApprovalBusyToast = null;
		/** @type {string} - The action the busy state is describing, for the toast copy. */
		this._ApprovalBusyAction = '';
		/** @type {any} - Timer that escalates the busy toast wording when the server goes quiet. */
		this._ApprovalBusyEscalationTimer = null;
		/** @type {boolean} - Whether a page reload has been requested and now owns the busy state. */
		this._ApprovalReloadPending = false;
	}

	/**
	 * Resolve once no save lifecycle is in flight.
	 *
	 * Unlike handleSaveAsync this never *requests* a save — it only waits for one that is already
	 * running. Callers that must not act on a half-written document (approval actions, which lock it)
	 * use this to close the window where the user could beat an in-flight save to the punch.
	 *
	 * @return {Promise<void>} Resolves immediately when nothing is saving.
	 */
	whenSaveSettled()
	{
		if (!this.IsSaving)
		{
			return Promise.resolve();
		}
		return new Promise((resolve) =>
		{
			this._SaveSettledWaiters.push(resolve);
		});
	}

	/**
	 * This function is used to get the style of the approval status based on the status of the document.
	 * @param {string} pStatus - The status of the document
	 * @return {object} - The icon class and background color for the status
	 * @example
	 * getApprovalStatusStyle('Draft');
	 * getApprovalStatusStyle('Submit');
	 * getApprovalStatusStyle('Approve');
	 */
	getApprovalStatusStyle(pStatus)
	{
		const returnObject = {};
		const documentStatus = pStatus || this.pict.AppData?.DocumentData?.ReportData?.Status;
		switch (documentStatus)
		{
		case 'Draft':
			returnObject.iconClass = 'fa-clipboard';
			returnObject.backgroundColor = 'has-background-grey';
			break;
		case 'Submit':
		case 'Submitted':
			returnObject.iconClass = 'fa-paper-plane';
			returnObject.backgroundColor = 'has-background-info-40';
			break;
		case 'Approve':
		case 'Approved':
			returnObject.iconClass = 'fa-check-circle';
			returnObject.backgroundColor = 'has-background-success-40';
			break;
		case 'Reject':
		case 'Rejected':
			returnObject.iconClass = 'fa-ban';
			returnObject.backgroundColor = 'has-background-danger-40';
			break;
		default:
			returnObject.iconClass = 'fa-question-circle';
			returnObject.backgroundColor = 'has-background-grey';
			break;
		}
		return returnObject;
	}

	/**
	 * This function is used to handle the change of the approval status of a document.
	 * @param {Event} event - The click event
	 * @param {string} action - The action to take on the document
	 * @return {void}
	 * @example
	 * handleApprovalStatusChange(event, 'Approve');
	 * handleApprovalStatusChange(event, 'Reject');
	 * handleApprovalStatusChange(event, 'Submit');
	 * handleApprovalStatusChange(event, 'Draft');
	 */
	handleApprovalStatusChange(event, action)
	{
		if (event)
		{
			event.preventDefault();
		}
		this.takeApprovalAction(action);
	}

	/**
	 * This function is used to determine if the open document action was called from a mobile device.
	 * @return {boolean} - True if the open document action was called from a mobile device, false otherwise
	 */
	isOpenDocumentCalledFromMobileDevice()
	{
		// @ts-ignore
		const platform = window.navigator?.platform || window.navigator?.userAgentData?.platform || '';
		const isMobileDevice = (window.navigator.userAgent.match(/(iPad|iPhone|iPod)/g) || platform.toUpperCase() === 'MACINTEL') && window.navigator.maxTouchPoints > 1;
		const isSafari = /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);
		if (isMobileDevice && !isSafari)
		{
			return true;
		}
		return false;
	}

	/**
	 * Open the Form Handler to show a confirmation modal when called from the webapp
	 * @param {object} event - The click event
	 * @param {number|string} [pIDDocument] - The ID of the Document to view
	 */
	//TODO: is this method in the right file as it calls a view ?
	openFormByIDDocumentHandler(event, pIDDocument)
	{
		const tmpIDDocument = pIDDocument || this.pict.AppData?.DocumentData?.ReportData?.IDDocument;
		if (this.isOpenDocumentCalledFromMobileDevice())
		{
			this.pict.views.DocumentDetails.confirmOpenForm(tmpIDDocument, '_top');
			return;
		}

		this.openFormByIDDocument(event, tmpIDDocument);
	}

	/**
	 * Opens a document in a new tab.
	 * @param {Object} event
	 * @param {Number} tmpIDDocument
	 * @param {String} [windowTarget='_blank'] - The target window for the document (default is '_blank')
	 */
	openFormByIDDocument(event, tmpIDDocument, windowTarget = '_blank')
	{
		const tmpElement = event.currentTarget;
		this.log.info('Open Form for Document ID:', tmpIDDocument);
		if (event)
		{
			tmpElement.classList.add('is-loading');
		}
		// Needed for handling ios issues.
		const windowReference = window.open('', windowTarget);
		const template =
		`<style> 
			@-webkit-keyframes load8 { 
				0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); } 
				100% { -webkit-transform: rotate(360deg); transform: rotate(360deg); } 
			} 
			@keyframes load8 { 
				0% { -webkit-transform: rotate(0deg); transform: rotate(0deg); } 
				100% { -webkit-transform: rotate(360deg);transform: rotate(360deg); } 
			} 
			*, *:before, *:after { 
				box-sizing: border-box;
			}
			body { 
				padding: 15px 0; 
				margin: 0 auto; 
				background-color: #53565A; 
				font-family: Arial, sans-serif; 
				width: 100%; 
				font-size: 15px; 
				max-width: 1000px;
			}
			.loading-splash-screen {
				width: 100%; 
				height: 100vh; 
				background: #53565A; 
				position: fixed; 
				top: 0; 
				left: 0; 
				text-align: center; 
				z-index: 10002; 
				display:flex; 
				align-items: center;
			}
			 .loading-splash-screen .loading-spinner, 
			 .loading-splash-screen .loading-spinner:after { 
			 	border-radius: 50%; width: 15em; height: 15em;
			}  
			.loading-spinner { 
				margin: 60px auto; 
				font-size: 10px; 
				position: relative; 
				text-indent: -9999em; 
				border-top: 1.1em solid rgba(255, 255, 255, 0.2); 
				border-right: 1.1em solid rgba(255, 255, 255, 0.2); 
				border-bottom: 1.1em solid rgba(255, 255, 255, 0.2); 
				border-left: 1.1em solid #ffffff; 
				-webkit-transform: translateZ(0); 
				-ms-transform: translateZ(0); 
				transform: translateZ(0); 
				-webkit-animation: load8 1.1s infinite linear; animation: load8 1.1s infinite linear;
			}
			</style>
			<div id="LoadingSplashScreen" class="loading-splash-screen">
				<div class="loading-spinner">Loading...</div>
			</div>
		`;
        windowReference.document.write(template);
		this.pict.HeadlightRestClient.getJSON('Report/Render/' + tmpIDDocument + '/default',
		(pError, pBody) =>
		{
			windowReference.location = window.location.origin + '/1.0/Report/' + pBody.GUIDReportDescription + '/index.html';
			if (event)
			{
				tmpElement.classList.remove('is-loading');
			}
			if (pError)
			{
				windowReference.close();
				this.log.error('Error retrieving Report Description:', pError);
				this.pict.views.HeadlightPanels?.toast?.({
					message: `<strong>Error retrieving Report Description:</strong><br/><em>${pError.message || pError}</em>`,
					type: 'is-danger',
				});
				return;
			}
		});
	}

	/**
	 * This function is used to handle the download of a document.
	 * @param {boolean} forceRender - Whether to force the document to render in the browser
	 * @param {object} [pReportData] - The report data to use when rendering the document (optional, used for "not my current document" scenarios)
	 * @param {Function} [pCallback] - The callback function to call when the download is complete
	 * @return {Promise<void>}
	 * @async
	 */
	async handleDocumentDownload(forceRender = false, pReportData = null, pCallback)
	{
		if (!this.IsDownloading)
		{
			this.IsDownloading = true;

			const tmpReportData = pReportData || this.AppData?.DocumentData?.ReportData;
			try
			{
				this.pict.views.HeadlightPanels.toast({
					message: 'Document download started...',
					type: 'is-success',
				});
				await this.services.HeadlightDocumentService.downloadDocument(forceRender, tmpReportData);
			}
			catch (e)
			{
				this.pict.log.error('Failed to download document', { error: e });
				this.pict.views.HeadlightPanels.toast({
					message: 'Failed to download document!',
					type: 'is-danger',
				});
			}
			finally
			{
				this.IsDownloading = false;
				// Call the callback function if provided
				if (pCallback)
				{
					pCallback();
				}
			}
		}
		else
		{
			this.pict.log.warn('Form processor not found or download operation is already in progress.');
		}
	}

	/**
	 * Fire-and-forget document save: requests a save and returns immediately — it does NOT wait for the
	 * Document PUT to land. Callers that must wait before proceeding (e.g. before locking on submit, or
	 * before reading the freshly-persisted copy) must use handleSaveAsync — which awaits the full lifecycle.
	 * `await handleSave(...)` is a no-op wait; not-async by design so that reads honestly.
	 * @param {boolean} [muteNotification] - Whether to mute the save notification.
	 * @return {void}
	 */
	handleSave(muteNotification = false)
	{
		this.handleSaveAsync(muteNotification);
	}

	/**
	 * Awaitable variant of handleSave.
	 *
	 * Resolves only after a save lifecycle carrying this caller's state has completed (including
	 * _saveDocument's PUT /Document/), so callers that must read the freshly-persisted server copy can
	 * guarantee the latest in-memory FormData has been flushed first. The form-conversion / PDF export is the
	 * motivating case: it downloads a server-rendered file filled from the persisted FormData, so it must
	 * save before fetching (PD-8714).
	 *
	 * Resolves with the save error (if any) rather than rejecting, so callers can decide whether to continue.
	 *
	 * Save lifecycles are serialized — only one runs at a time. A caller arriving while a save is in flight
	 * (a double-clicked Save button, or a background trigger firing mid-save) does not start a second
	 * lifecycle; it queues exactly one trailing save that runs after the in-flight one drains, and so carries
	 * whatever it mutated. Coalescing onto the in-flight save instead would silently drop those mutations,
	 * because callers mutate ReportData/FormData immediately before asking for a save.
	 *
	 * @param {boolean} [muteNotification] - Whether to mute the save toast.
	 * @return {Promise<Error|undefined>} Resolves once a save covering this request has completed.
	 */
	handleSaveAsync(muteNotification = false)
	{
		if (this.IsSaving)
		{
			this._SavePending = true;
			this._PendingMuteNotification = this._PendingMuteNotification && muteNotification;
			return new Promise((resolve) =>
			{
				this._PendingSaveWaiters.push(resolve);
			});
		}
		return this._drainSaveQueue(muteNotification);
	}

	/**
	 * Run save lifecycles until nothing further has been requested, holding the single-flight latch across
	 * the whole drain so the Save button does not flicker between queued cycles.
	 * @param {boolean} pMuteNotification - Whether to mute the toast for the first cycle.
	 * @return {Promise<Error|undefined>} Resolves with the error from the last cycle run, if any.
	 */
	async _drainSaveQueue(pMuteNotification)
	{
		this.IsSaving = true;
		this._syncSaveButtonState();
		// Approval actions must not be reachable while the document is being written; see
		// _syncApprovalActionState.
		this._syncApprovalActionState();
		let tmpMuteNotification = pMuteNotification;
		let tmpCycleCount = 0;
		/** @type {Error|undefined} */
		let tmpError;
		try
		{
			do
			{
				// Snapshot before running: these waiters queued during the previous cycle, so the cycle about
				// to run is the one that carries their mutation.
				this._SavePending = false;
				const tmpWaiters = this._PendingSaveWaiters;
				this._PendingSaveWaiters = [];
				tmpError = await this._runSaveLifecycle(tmpMuteNotification);
				tmpMuteNotification = this._PendingMuteNotification;
				this._PendingMuteNotification = true;
				for (const fWaiter of tmpWaiters)
				{
					fWaiter(tmpError);
				}
				tmpCycleCount++;
				if (tmpCycleCount >= MAX_CONSECUTIVE_SAVE_CYCLES && this._SavePending)
				{
					this.pict.log.error(`Document save queue drained ${tmpCycleCount} consecutive cycles and is still being re-queued; abandoning the queued save to avoid a save loop.`);
					break;
				}
			}
			while (this._SavePending);
		}
		finally
		{
			this.IsSaving = false;
			this._SavePending = false;
			this._PendingMuteNotification = true;
			const tmpOrphanedWaiters = this._PendingSaveWaiters;
			this._PendingSaveWaiters = [];
			for (const fWaiter of tmpOrphanedWaiters)
			{
				fWaiter(tmpError);
			}
			this._syncSaveButtonState();
			this._syncApprovalActionState();
			this._syncCurrentSamplesButtonState();
			const tmpSettledWaiters = this._SaveSettledWaiters;
			this._SaveSettledWaiters = [];
			for (const fSettledWaiter of tmpSettledWaiters)
			{
				fSettledWaiter();
			}
			// Restart the auto-save countdown from this save, so a manual save buys the user a full
			// interval rather than an auto-save moments later.
			this.pict.providers?.AutoSave?.onSaveLifecycleSettled?.(tmpError);
		}
		return tmpError;
	}

	/**
	 * Execute one full save lifecycle. Never rejects; resolves with the error when one occurred.
	 *
	 * The lifecycle is an Anticipate waterfall fanned out across providers, and it stalls forever if any hook
	 * fails to invoke its callback — a hung socket in FormEntityCreator's Document PUT is the realistic case.
	 * The watchdog releases the latch on a stall so the user is not stranded behind a permanently disabled
	 * Save button with no indication of what happened.
	 * @param {boolean} pMuteNotification - Whether to mute the save toast.
	 * @return {Promise<Error|undefined>}
	 */
	_runSaveLifecycle(pMuteNotification)
	{
		this._MuteNotification = pMuteNotification;
		const tmpUploadID = this.pict.providers.UploadTracking.trackUpload();
		// Falls back rather than trusting options: a missing value would otherwise become a 0ms timeout and
		// make every save look like it stalled.
		const tmpWatchdogTimeoutMS = this.options.SaveWatchdogTimeoutMS || 120000;
		return new Promise((resolve) =>
		{
			let tmpSettled = false;
			/** @type {any} */
			let tmpWatchdogTimer = null;
			/**
			 * @param {Error} [pError] - The error the lifecycle (or the watchdog) produced.
			 * @return {void}
			 */
			const fSettle = (pError) =>
			{
				if (tmpSettled)
				{
					return;
				}
				tmpSettled = true;
				if (tmpWatchdogTimer)
				{
					clearTimeout(tmpWatchdogTimer);
					tmpWatchdogTimer = null;
				}
				this.pict.providers.UploadTracking.untrackUpload(tmpUploadID);
				return resolve(pError);
			};
			tmpWatchdogTimer = setTimeout(() =>
			{
				const tmpTimeoutError = new Error(`Document save did not complete within ${tmpWatchdogTimeoutMS}ms.`);
				this.pict.log.error('Document save watchdog fired; releasing the save lock.', { error: tmpTimeoutError });
				// Surfaced even when the save was muted — a stalled background save is exactly the condition
				// the user otherwise has no way to see.
				this.pict.views?.HeadlightPanels?.toast({
					message: 'The server has not responded to the save. Your latest changes may not be saved.',
					type: 'is-warning',
				});
				return fSettle(tmpTimeoutError);
			}, tmpWatchdogTimeoutMS);
			this.pict.PictApplication.saveDataAsync(fSettle);
		});
	}

	/**
	 * Push the current save state onto the navbar Save button. The button belongs to the panels view and is
	 * re-rendered whenever the document context changes, so that render path calls this too.
	 * @return {void}
	 */
	_syncSaveButtonState()
	{
		const tmpPanelsView = this.pict.views?.HeadlightPanels;
		if (tmpPanelsView && typeof tmpPanelsView.syncSaveButtonState === 'function')
		{
			tmpPanelsView.syncSaveButtonState();
		}
	}

	/**
	 * Push the current save/approval state onto the approval action buttons in the Status panel.
	 *
	 * The buttons live in a panel that is built on demand and rebuilt whenever the approval state is
	 * re-fetched, so both this and that build path apply the state. Derives entirely from provider
	 * state, so calling it before the panel exists is a no-op that the next build picks up.
	 * @return {void}
	 */
	_syncApprovalActionState()
	{
		const tmpStatusPane = this.pict.views?.HeadlightPaneStatus;
		if (tmpStatusPane && typeof tmpStatusPane.syncApprovalActionButtonState === 'function')
		{
			tmpStatusPane.syncApprovalActionButtonState();
		}
	}

	/**
	 * Latch (or release) the approval busy state and repaint everything that reflects it.
	 *
	 * Latch first, then repaint: the latch is what stands auto-save down and blocks a second entry into
	 * takeApprovalAction; the repaints are what tell the user. Every _sync below derives entirely from
	 * the latch, so each is a safe no-op before its DOM exists.
	 * @param {boolean} pIsBusy - Whether an approval action is running.
	 * @param {string} [pAction] - The action being taken, used for the toast copy.
	 * @return {void}
	 */
	_setApprovalBusy(pIsBusy, pAction)
	{
		// A reload is committing. Releasing now would re-enable a form that is about to be replaced and
		// take away the only explanation the user has for the wait; see _beginApprovalReload.
		if (!pIsBusy && this._ApprovalReloadPending)
		{
			return;
		}
		this.isTakingAction = Boolean(pIsBusy);
		this._ApprovalBusyAction = pIsBusy ? (pAction || '') : '';
		this._syncDocumentFormBusyState();
		this._syncSaveButtonState();
		this._syncApprovalActionState();
		this._syncApprovalBusyToast();
	}

	/**
	 * Make the document form inert while an approval action runs.
	 *
	 * `inert` is what actually stops the keyboard — pointer-events only stops the mouse, and almost
	 * nothing in a manifest-driven form carries a disabled attribute. The class goes on the body rather
	 * than the form because Select2 dropdowns and flatpickr calendars portal outside it.
	 * @return {void}
	 */
	_syncDocumentFormBusyState()
	{
		const tmpFormElement = document.getElementById('Headlight-Document-Form');
		if (!tmpFormElement)
		{
			return;
		}
		if (this.isTakingAction)
		{
			// Blur before going inert so focus lands on the body rather than being dropped mid-subtree.
			if (tmpFormElement.contains(document.activeElement) && typeof document.activeElement?.blur === 'function')
			{
				document.activeElement.blur();
			}
			document.body.classList.add('hl-approval-busy');
			tmpFormElement.setAttribute('aria-busy', 'true');
			tmpFormElement.setAttribute('inert', '');
		}
		else
		{
			document.body.classList.remove('hl-approval-busy');
			tmpFormElement.setAttribute('aria-busy', 'false');
			tmpFormElement.removeAttribute('inert');
		}
	}

	/**
	 * Compose the busy-toast copy for an approval action.
	 * @param {string} pAction - The action being taken.
	 * @return {string}
	 */
	_getApprovalBusyMessage(pAction)
	{
		// hasOwnProperty, not a bare lookup: the action name is server-defined, so "constructor" or
		// "toString" would otherwise resolve off Object.prototype and render a function into the toast.
		const tmpKey = String(pAction || '').toLowerCase();
		if (Object.prototype.hasOwnProperty.call(APPROVAL_BUSY_PHRASE, tmpKey))
		{
			return APPROVAL_BUSY_PHRASE[tmpKey] + APPROVAL_BUSY_SUFFIX;
		}
		// A server-defined action name. Name it verbatim rather than guessing a verb for it.
		return (pAction ? `Applying the ${pAction} action to this document.` : 'Updating the document status.') + APPROVAL_BUSY_SUFFIX;
	}

	/**
	 * Show or tear down the persistent toast that explains the busy state.
	 *
	 * Sticky, because the toasts this action itself fires (a failed save, a validation error, the save
	 * watchdog) would otherwise evict the one explanation the user has for a page that stopped responding.
	 * @return {void}
	 */
	_syncApprovalBusyToast()
	{
		if (this._ApprovalBusyEscalationTimer)
		{
			clearTimeout(this._ApprovalBusyEscalationTimer);
			this._ApprovalBusyEscalationTimer = null;
		}
		const tmpPanelsView = this.pict.views?.HeadlightPanels;
		if (!this.isTakingAction)
		{
			if (this._ApprovalBusyToast)
			{
				tmpPanelsView?.dismissToast?.(this._ApprovalBusyToast);
				this._ApprovalBusyToast = null;
			}
			tmpPanelsView?.announceStatus?.('');
			return;
		}
		if (this._ApprovalBusyToast || typeof tmpPanelsView?.toast !== 'function')
		{
			return;
		}
		const tmpMessage = this._getApprovalBusyMessage(this._ApprovalBusyAction);
		this._ApprovalBusyToast = tmpPanelsView.toast(
			{
				message: tmpMessage,
				type: 'is-info',
				sticky: true,
				// No dismiss button: closing it would leave the page locked with nothing left saying why.
				dismissible: false,
				// The action name comes from the server; never let it reach the DOM as markup.
				allowHTML: false,
			});
		tmpPanelsView.announceStatus?.(tmpMessage);
		this._ApprovalBusyEscalationTimer = setTimeout(() => this._escalateApprovalBusyToast(), this.options.ApprovalBusyEscalationMS || APPROVAL_BUSY_ESCALATION_MS);
	}

	/**
	 * Swap the busy toast for a "still waiting" one. Deliberately does not release the lock: the action
	 * may still land server-side, and re-arming auto-save and the action buttons underneath it is exactly
	 * the race the save guard closed.
	 * @return {void}
	 */
	_escalateApprovalBusyToast()
	{
		this._ApprovalBusyEscalationTimer = null;
		const tmpPanelsView = this.pict.views?.HeadlightPanels;
		if (!this.isTakingAction || this._ApprovalReloadPending || typeof tmpPanelsView?.toast !== 'function')
		{
			return;
		}
		this.pict.log.warn(`Approval action "${this._ApprovalBusyAction}" has not responded yet; escalating the busy notice.`);
		tmpPanelsView.dismissToast?.(this._ApprovalBusyToast);
		const tmpMessage = 'The server has not responded yet. Keep this page open; it will refresh as soon as the action finishes.';
		this._ApprovalBusyToast = tmpPanelsView.toast({ message: tmpMessage, type: 'is-warning', sticky: true, dismissible: false, allowHTML: false });
		tmpPanelsView.announceStatus?.(tmpMessage);
	}

	/**
	 * Latch the reload phase of an approval action, then reload.
	 *
	 * window.location.reload() does not stop the caller — the finally in takeApprovalAction still runs and
	 * the page keeps executing until the navigation commits. The latch makes that release a no-op, so the
	 * form stays inert and the toast stays up right through the fetch. Extracted so tests can drive the
	 * success path without navigating.
	 * @return {void}
	 */
	_beginApprovalReload()
	{
		this._ApprovalReloadPending = true;
		if (this._ApprovalBusyEscalationTimer)
		{
			clearTimeout(this._ApprovalBusyEscalationTimer);
			this._ApprovalBusyEscalationTimer = null;
		}
		const tmpPanelsView = this.pict.views?.HeadlightPanels;
		if (typeof tmpPanelsView?.toast === 'function')
		{
			// The action landed; what is left is the reload. Say so, so a slow refresh does not read as a hang.
			tmpPanelsView.dismissToast?.(this._ApprovalBusyToast);
			const tmpMessage = 'Status updated. Refreshing the page...';
			this._ApprovalBusyToast = tmpPanelsView.toast({ message: tmpMessage, type: 'is-success', sticky: true, dismissible: false, allowHTML: false });
			tmpPanelsView.announceStatus?.(tmpMessage);
		}
		window.location.reload();
	}

	/**
	 * Re-check whether this document has joined Samples once a save settles, so the navbar Current Samples
	 * button appears without a full view refresh. The join is created server-side, so it cannot be derived
	 * from local state at creation time.
	 * @return {void}
	 */
	_syncCurrentSamplesButtonState()
	{
		const tmpPanelsView = this.pict.views?.HeadlightPanels;
		if (tmpPanelsView && typeof tmpPanelsView.syncCurrentSamplesButtonState === 'function')
		{
			tmpPanelsView.syncCurrentSamplesButtonState();
		}
	}

	// eslint-disable-next-line valid-jsdoc
	/**
	 * This function is used to tie the document save for forms into the application save lifecycle.
	 * @param {(pError?: Error) => void} fCallback - The callback to call after the data pre-load.
	 */
	onBeforeSaveDataAsync(fCallback)
	{
		let onBeforeSavePromise = Promise.resolve();
		//FIXME: migrate this to the new lifecycle events and sunset this
		if (typeof this.pict.PictApplication.onBeforeDocumentSave === 'function')
		{
			onBeforeSavePromise = this.pict.PictApplication.onBeforeDocumentSave();
			if (typeof onBeforeSavePromise.then !== 'function')
			{
				onBeforeSavePromise = Promise.resolve();
			}
		}
		onBeforeSavePromise.then(() =>
		{
			return super.onBeforeSaveDataAsync(fCallback);
		}).catch((error) =>
		{
			this.pict.log.error('Error in onBeforeDocumentSave:', error);
			// Proceed with the save operation even if onBeforeDocumentSave fails
			return super.onBeforeSaveDataAsync(fCallback);
		});
	}

	// eslint-disable-next-line valid-jsdoc
	/**
	 * Ties the document save for forms into the application save lifecycle.
	 * @param {(pError?: Error) => void} fCallback - The callback to call after the data load.
	 */
	onSaveDataAsync(fCallback)
	{
		this._saveDocument().then(() =>
		{
			return super.onSaveDataAsync(fCallback);
		}).catch((err) =>
		{
			return super.onSaveDataAsync((pParentError) =>
			{
				return fCallback(err || pParentError);
			});
		});
	}

	/**
	 * Persist the document, then run the application's post-save hook.
	 *
	 * Rethrows a save failure so it reaches the save-data lifecycle callback and, through it, the value
	 * handleSaveAsync resolves with — callers that gate on a successful save (submit, which locks the
	 * document) cannot honour a failure they were never told about. Because the lifecycle waterfall
	 * short-circuits on error, a failed save also skips the remaining onAfterSaveDataAsync hooks, so entity
	 * comprehension is not pushed against a document whose own write did not land.
	 * @return {Promise<void>}
	 */
	async _saveDocument()
	{
		// The baseline advances on the server having seen the payload, not on the record being persisted.
		// It describes what the comprehension has already asked the server to write, and the server is the
		// only thing that applies it - so on the local transport the baseline must stay where it is and
		// every save re-emits everything changed since load. Folding on a local write instead would have
		// the next save's prune drop the records the first save emitted, because they now match the
		// baseline, and only the final document is ever synced.
		const tmpDocumentService = this.pict.services.HeadlightDocumentService;
		const tmpIsServerTransport = (typeof tmpDocumentService.isLocalDocumentTransport !== 'function') ||
			!tmpDocumentService.isLocalDocumentTransport();
		try
		{
			// Fold every provider's emission into the delta baseline ONCE, here, immediately before the
			// PUT: the baseline lives in FormData, and FormData is snapshotted into the PUT body, so a fold
			// after the save would never reach the server and every reload would start from a stale one.
			if (tmpIsServerTransport)
			{
				EntityDataBaseline.foldPendingEmission(this.pict);
			}
			const tmpSavedRecord = await this.pict.services.HeadlightDocumentService.saveDocument();
			this._adoptResolvedRecordIdentities(tmpSavedRecord);
			if (tmpIsServerTransport)
			{
				this._releaseUnappliedRecords(tmpSavedRecord);
			}
		}
		catch (pSaveError)
		{
			// The delta baseline was folded forward before this PUT so it would ride the same payload. The
			// PUT did not land, so it is now claiming records were written that may not have been -
			// discarding it makes the retry project everything whole rather than silently emitting nothing.
			// Nothing was folded on the local transport, so there is nothing to unwind.
			if (tmpIsServerTransport)
			{
				EntityDataBaseline.clearBaseline(this.pict);
			}
			this.pict.log.error('Failed to save document', { error: pSaveError });
			if (!this._MuteNotification)
			{
				this.pict.views.HeadlightPanels.toast({
					message: 'Failed to save document!',
					type: 'is-danger',
				});
			}
			throw pSaveError;
		}
		if (!this._MuteNotification)
		{
			this.pict.views.HeadlightPanels.toast({
				message: 'Document saved successfully!',
				type: 'is-success',
			});
		}
		// The document is already persisted by this point, so a failure in the post-save hook is not a save
		// failure and must not be reported as one.
		if (typeof this.pict.PictApplication.onAfterDocumentSave === 'function')
		{
			try
			{
				await this.pict.PictApplication.onAfterDocumentSave();
			}
			catch (pAfterSaveError)
			{
				this.pict.log.error('Document saved, but onAfterDocumentSave failed', { error: pAfterSaveError });
			}
		}
	}

	/**
	 * Adopt the row ids the server resolved for records this save created.
	 *
	 * The form mints GUIDs and the server resolves them to rows; until the ids come back the form
	 * cannot tell a record it created from one it has never saved, and re-asserts every field of it on
	 * every subsequent save. Runs here rather than in the input providers' post-save hook because it has
	 * to land BEFORE those hooks fold the emission into the delta baseline.
	 *
	 * Absent on any path the server does not decorate - bulk writes, offline replay, a form not on the
	 * scoped path - and a save with nothing to resolve. All of those simply keep today's behaviour.
	 *
	 * @param {Record<string, any>} [pSavedRecord] - The document record the save returned.
	 * @return {void}
	 */
	_adoptResolvedRecordIdentities(pSavedRecord)
	{
		const tmpIdentities = pSavedRecord && pSavedRecord.ComprehensionRecordIdentities;
		if (!tmpIdentities || typeof tmpIdentities !== 'object')
		{
			return;
		}
		try
		{
			const tmpAdopted = EntityDataBaseline.applyResolvedIdentities(this.pict, tmpIdentities);
			// Trace, not info: one line per save is noise in normal operation, but the count is the only
			// thing that separates "the save carried no identities" from "it carried some and none matched
			// a held record", which is worth having when a form starts re-emitting records it should not.
			this.pict.log.trace(`DocumentProvider: took ${tmpAdopted} server-resolved record id(s) from the save.`);
		}
		catch (pError)
		{
			this.pict.log.error(`DocumentProvider: could not adopt the server-resolved record ids; the next save may re-assert those records whole: ${pError.message || pError}`);
		}
	}

	/**
	 * Un-baseline the records this save asked for and the server refused, so the next save sends them again.
	 *
	 * The baseline was folded before the PUT and already counts them as written. The save response carries
	 * the document's `EntityData` with the new `ExecutionLog` entry, which is the source of record for what
	 * landed.
	 *
	 * @param {Record<string, any>} [pSavedRecord] - The document record the save returned.
	 * @return {void}
	 */
	_releaseUnappliedRecords(pSavedRecord)
	{
		if (!pSavedRecord || typeof pSavedRecord !== 'object')
		{
			return;
		}
		try
		{
			EntityDataBaseline.releaseUnappliedRecords(this.pict, pSavedRecord.EntityData || pSavedRecord.EntityDataJSON);
		}
		catch (pError)
		{
			this.pict.log.error(`DocumentProvider: could not read which records the save left unapplied; they will not be retried until the document is reopened: ${pError.message || pError}`);
		}
	}

	/**
	 * This function is used to take an approval action on a document.
	 * @param {string} action - The action to take on the document
	 * @return {Promise<void>}
	 * @async
	 * @example
	 * takeApprovalAction('Approve');
	 * takeApprovalAction('Reject');
	 * takeApprovalAction('Submit');
	 */
	async takeApprovalAction(action)
	{
		if (!this.isTakingAction)
		{
			// Latch and lock before the first await — the user needs to know something is happening at
			// click time, not after the save settles. The latch is also what stands auto-save down and
			// stops a second click landing.
			this._setApprovalBusy(true, action);
			let isFormValid = true;

			try
			{
				// An approval action changes (and often locks) the document server-side. Acting while a
				// save is still in flight lets the lock land first, which rejects the resolving save and
				// loses whatever it carried -- the "beat the clock" race. The buttons are disabled for
				// the duration of a save, so reaching this await means something bypassed the UI (an
				// auto-save that started between the click and the handler, a keyboard activation, a
				// programmatic call); wait it out rather than racing it.
				await this.whenSaveSettled();
				// toggle the panel before rendering the new status
				this.pict.views.HeadlightPanels.handlePanelToggle('Status');

				// if the action is to submit the form, we need to check validation before proceeding
				//FIXME: these being hard-coded is no-bueno
				if (action?.toLowerCase() === 'submit' || action?.toLowerCase() === 'approve')
				{
					// if the action is to submit the form, we need to check validation before proceeding
					if (action?.toLowerCase() === 'submit')
					{
						// Await the full save (Document PUT) BEFORE the approval action locks the doc: if the lock
						// lands first the resolving save is rejected and the GUID->id resolution is lost. Abort the
						// submit on save failure rather than lock over unsaved state.
						const tmpSaveError = await this.handleSaveAsync(true);
						if (tmpSaveError)
						{
							this.log.error('Aborting submit: the document save failed', { error: tmpSaveError });
							this.pict.views.HeadlightPanels.toast({
								message: `<strong>Failed to save document; can't submit.</strong><br/><em>${tmpSaveError.message || tmpSaveError}</em>`,
								type: 'is-danger',
							});
							return;
						}
					}
					// All form-validation logic + styling is delegated to the
					// InputValidation provider so it stays consistent with
					// the per-input flows (visibility-aware required sync,
					// correct ancestor traversal via closest(), Select2
					// widget mirroring, data-required hidden-bearer value
					// checks for custom widgets like Signature, aria-invalid
					// sync, etc).
					const formElement = document.getElementById('Headlight-Document-Form');
					const inputValidationProvider = this.pict.providers.InputValidation;
					const hasInputValidationProvider = inputValidationProvider
						&& typeof inputValidationProvider.preSubmitVisibilitySweep === 'function'
						&& typeof inputValidationProvider.applyFormSubmissionStyling === 'function';

					if (formElement && hasInputValidationProvider)
					{
						// Step 1 — sync `required` on every manifest-required
						// input based on current visibility. Hidden-tab
						// fields lose `required`, fields that came back
						// into view get it restored. Keeps the browser's
						// native checkValidity() honest.
						inputValidationProvider.preSubmitVisibilitySweep(formElement);

						// Step 2 — apply styling across the form and let the
						// provider tell us whether anything visible is still
						// in a failing state. Provider handles all the
						// per-input edge cases (Select2 widget mirroring,
						// hidden data-bearer value checks, etc).
						const tmpProviderSaysValid = inputValidationProvider.applyFormSubmissionStyling(formElement);
						const tmpNativeInvalid = formElement instanceof HTMLFormElement && !formElement.checkValidity();

						if (tmpNativeInvalid || !tmpProviderSaysValid)
						{
							formElement.classList.add('show-validation');
							this.pict.views.HeadlightPanels.toast({
								message: 'Form validation failed. Please correct the errors and try again.',
								type: 'is-danger',
							});
							isFormValid = false;
						}
					}
					else if (formElement instanceof HTMLFormElement && !formElement.checkValidity())
					{
						// Fallback when the InputValidation provider isn't
						// available — purely native checks, no styling
						// niceties.
						formElement.classList.add('show-validation');
						this.pict.views.HeadlightPanels.toast({
							message: 'Form validation failed. Please correct the errors and try again.',
							type: 'is-danger',
						});
						isFormValid = false;
					}
				}
				// if the form is verfied as valid (on submission checks), then proceed with the action
				if (isFormValid)
				{
					// remove the validation class from the form
					document.getElementById('Headlight-Document-Form')?.classList.remove('show-validation');
					// send the action to the service to update the document status
					const response = await this.services.HeadlightDocumentService.doApprovalAction(action);
					this.AppData.DocumentData.ReportData.Status = response.Status;
					// TODO: replace reload by getting view rerender to work correctly
					// rerendering the views introduces new bugs
					// reload page to update nav bar and signature section
					this._beginApprovalReload();
				}
			}
			catch (e)
			{
				this.pict.log.error('Failed to change the document status', { error: e });
				// post a note to the user that the action failed
				this.pict.views.HeadlightPanels.toast({
					message: 'Failed to change the document status!',
					type: 'is-danger',
				});
			}
			finally
			{
				// A no-op once a reload is committing, so the lock and its toast survive the navigation.
				this._setApprovalBusy(false);
			}
		}
		else
		{
			this.pict.log.warn('Approval action is already in progress.');
		}
	}

	/**
	 * This method is sued to view the document file as a PDF or JSON blob in a new tab.
	 * @param {string} pTarget - The target for the document file
	 * @param {boolean} asRawJSON - Whether to view the document file as raw JSON
	 * @param {number} [passedID] - If an id is passed, view that document file instead.
	 * @return {Promise<Window>}
	 * @example
	 * viewDocumentFile('_blank', false);
	 */
	viewDocumentFile(pTarget, asRawJSON = false, passedID)
	{
		return new Promise((resolve, reject) =>
		{
			const documentId = passedID || this.AppData?.DocumentData?.ReportData?.IDDocument;

			if (!documentId)
			{
				return reject(new Error('Document ID is missing'));
			}

			const extension = asRawJSON ? 'json' : 'pdf';

			try
			{
				const openedWindow = window.open(`${this.options.HeadlightAPIURL}Document/${documentId}/DCR.${extension}`, pTarget || '_blank');
				if (openedWindow)
				{
					return resolve(openedWindow);
				}
				else
				{
					return reject(new Error('Failed to open document'));
				}
			}
			catch (e)
			{
				return reject(e);
			}
		});
	}

	/**
	 * This method is used to get the document record for a specific document id.
	 * @param {any} pIDDocument - The document id to get the record for
	 * @param {Function} [pCallback] - The callback function to call when the document record is retrieved
	 * @return {Promise<Object>}
	 */
	async getDocument(pIDDocument, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Document/${pIDDocument}`,
					(pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Document: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Document: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * This method is used to get the document record for a specific document guid.
	 * @param {any} pGUIDDocument - The document guid to get the record for
	 * @param {Function} pCallback - The callback function to call when the document record is retrieved
	 * @return {Promise<Object>}
	 */
	async getDocumentByGUID(pGUIDDocument, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`Documents/By/GUIDDocument/${pGUIDDocument}`,
					(pError, pBody) =>
					{
						if (pError || !pBody.length)
						{
							this.pict.log.error(`Error fetching Document: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							resolve(pBody[0]);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(pBody[0]);
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Document: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Add a polyjoin for a remote entity to a document (only if it does not already exist).
	 * @param {Number} pIDDocument - The ID of the Document being added to.
	 * @param {Number} pIDRemote - The ID of the RemoteEntity to get Documents for
	 * @param {Number} pRemoteType - The Type of Entity to get Documents for
	 * @param {Function} [pCallback] - The callback function to call when the Documents are fetched
	 * @return {Promise}
	 */
	async addRemoteToDocument(pIDDocument, pIDRemote, pRemoteType, pCallback)
	{
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`DocumentPolyJoins/FilteredTo/FBV~IDDocument~EQ~${ pIDDocument }~FBV~IDRemote~EQ~${ pIDRemote }~FBV~RemoteType~EQ~${ pRemoteType }~FSF~CreateDate~DESC~0`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error joining to document: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
							this.pict.providers.UploadTracking.untrackUpload(uploadID);
						}
						else
						{
							if (pBody.length)
							{
								resolve(true);
								if (pCallback)
								{
									pCallback(true);
								}
								this.pict.providers.UploadTracking.untrackUpload(uploadID);
							}
							else
							{
								this.pict.fable.services.HeadlightRestClient.postJSON('DocumentPolyJoin',
									{
										RemoteType: pRemoteType,
										IDRemote: pIDRemote,
										IDDocument: pIDDocument,
									},
									async (pError, pBody) =>
									{
										if (pError)
										{
											this.pict.log.error(`Error joining to document: `, pError);
											reject(pError);
											// if we have a callback, run it now
											if (pCallback)
											{
												pCallback(null);
											}
										}
										else
										{
											resolve(true);
											if (pCallback)
											{
												pCallback(true);
											}
										}
										this.pict.providers.UploadTracking.untrackUpload(uploadID);
									});
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error joining to document: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
			this.pict.providers.UploadTracking.untrackUpload(uploadID);
		}
	}

	/**
	 * Get Documents by a DocumentPolyJoin Remote ID
	 * @param {Number} pIDRemote - The ID of the RemoteEntity to get Documents for
	 * @param {Number} pRemoteType - The Type of Entity to get Documents for
	 * @param {Function} [pCallback] - The callback function to call when the Documents are fetched
	 * @return {Promise}
	 */
	async getDocumentsByRemoteID(pIDRemote, pRemoteType, pCallback)
	{
		try
		{
			return await new Promise((resolve, reject) =>
			{
				this.pict.fable.services.HeadlightRestClient.getJSON(`DocumentPolyJoins/FilteredTo/FBV~IDRemote~EQ~${ pIDRemote }~FBV~RemoteType~EQ~${ pRemoteType }~FSF~CreateDate~DESC~0`,
					async (pError, pBody) =>
					{
						if (pError)
						{
							this.pict.log.error(`Error fetching Documents: `, pError);
							reject(pError);
							// if we have a callback, run it now
							if (pCallback)
							{
								pCallback(null);
							}
						}
						else
						{
							if (pBody.length)
							{
								// Batch-fetch every joined Document in ONE request (no Count)
								// instead of one Document/{id} GET per join. This N+1 used to
								// dominate Sample-open time when a sample had many documents.
								const tmpDocumentIDs = pBody.map((pJoin) => pJoin.IDDocument);
								this.pict.fable.services.HeadlightRestClient.getJSON(`Documents/FilteredTo/FBL~IDDocument~INN~${ tmpDocumentIDs.join(',') }/0/${ tmpDocumentIDs.length }`,
									(pDocumentError, pDocumentBody) =>
									{
										if (pDocumentError)
										{
											this.pict.log.error(`Error fetching Documents: `, pDocumentError);
											reject(pDocumentError);
											if (pCallback)
											{
												pCallback(null);
											}
											return;
										}
										// Preserve the CreateDate-DESC order from the join query.
										const tmpDocumentsByID = {};
										for (const tmpDocument of (pDocumentBody || []))
										{
											tmpDocumentsByID[tmpDocument.IDDocument] = tmpDocument;
										}
										const fullDocuments = tmpDocumentIDs.map((pID) => tmpDocumentsByID[pID]).filter((pDocument) => pDocument);
										resolve(fullDocuments);
										if (pCallback)
										{
											pCallback(fullDocuments);
										}
									});
							}
							else
							{
								resolve([]);
								if (pCallback)
								{
									pCallback([]);
								}
							}
						}
					});
			});
		}
		catch (error)
		{
			this.pict.log.error(`Error fetching Documents: `, error);
			// if we have a callback, run it now
			if (pCallback)
			{
				pCallback(null);
			}
		}
	}

	/**
	 * Get the Documents
	 * @return {Promise<void>}
	 * @async
	 * @example
	 */
	async getDocuments()
	{
		return new Promise((resolve, reject) =>
		{
			let tmpURL = 'Documents';

			// Filter by customer if IDCustomer is present
			if (this.pict.AppData?.DocumentData?.ReportData?.IDCustomer)
			{
				tmpURL = `Documents/By/IDCustomer/${this.pict.AppData?.DocumentData?.ReportData?.IDCustomer}`;
			}

			this.pict.HeadlightRestClient.getJSON(tmpURL, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error('Failed to get Documents list.', pError);
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Get the Document search count
	 * @param {Object} pSearchQuery - The search query object to use when performing the search
	 * @return {Promise<number>}
	 * @async
	 * @example
	 * getDocumentSearchCount({ IDProject: 1234 }, (count) => { console.log(count); });
	 */
	async getDocumentSearchCount(pSearchQuery)
	{
		const tmpFilter = this.pict.providers.UtilityProvider.generateMeadowFilter(pSearchQuery);
		const tmpURL = `Documents/Count${tmpFilter ? `/${tmpFilter}` : ''}`;

		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(tmpURL, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing request for Count: ${pError}`, pError);
					reject(pError);
				}
				else
				{
					resolve(pBody?.Count || 0);
				}
			});
		});
	}

	/**
	 * Get the Document search results
	 * @param {Object} pSearchQuery - The search query object to use when performing the search
	 * @return {Promise<Array<any>>}
	 * @async
	 * @example
	 * getDocumentSearchResults({ IDProject: 1234 }, (results) => { console.log(results); });
	 */
	async getDocumentSearchResults(pSearchQuery)
	{
		const tmpURL = `Documents/` + this.pict.providers.UtilityProvider.generateMeadowFilter(pSearchQuery);
		const startCount = (pSearchQuery.pagination.start - 1) * pSearchQuery.pagination.limit;
		const endCount = pSearchQuery.pagination.start * pSearchQuery.pagination.limit;
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`${tmpURL}/${startCount}/${endCount}`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing search: ${pError}`, pError);
					reject(pError);
				}
				if (!Array.isArray(pBody))
				{
					//FIXME: ...
					return resolve([]);
				}
				resolve(pBody);
			});
		});
	}

	/**
	 * Returns the current time formatted as HH:mm:ss (using dayjs)
	 * @return {string} The current time formatted as HH:mm:ss
	 */
	getDocumentDateCurrentTime()
	{
		return ` ${ this.fable.Dates.dayJS(new Date()).format('HH:mm:ss') }`;
	}

	/**
	 * This method performs an Document search based on the current search filters.
	 * @param {Object} pOptions - The options to use when performing the search.
	 * @return {Promise<{ results: any, count: number }>}
	 * @async
	 */
	async performDocumentSearch(pOptions = {})
	{
		return new Promise((resolve, reject) =>
		{
			if (!pOptions)
			{
				this.pict.log.error('No search options provided.');
				return reject(new Error('No search options provided.'));
			}

			const tmpSearchQuery = {
				filters: [],
				pagination: {
					start: pOptions?.CurrentPage || 1,
					limit: pOptions?.PageSize || 25,
				},
				sort: {
					field: pOptions.SortBy || 'DocumentDate',
					direction: pOptions.SortDirection || 'DESC',
				},
			};

			// handle the search filters
			if (pOptions?.SearchText && pOptions.SearchText?.trim().length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'Name',
					comparator: 'LK',
					target: pOptions.SearchText,
				});
			}

			// handle the search filters
			if (pOptions?.Status && pOptions.Status?.trim().length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'Status',
					comparator: 'LK',
					target: pOptions.Status,
				});
			}

			if (pOptions?.Projects && pOptions.Projects.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'IDProject',
					comparator: pOptions.Projects.length === 1 ? 'EQ' : 'INN',
					target: pOptions.Projects.length === 1 ? pOptions.Projects[0] : pOptions.Projects,
				});
			}

			if (pOptions?.Users && pOptions.Users.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'CreatingIDUser',
					comparator: pOptions.Users.length === 1 ? 'EQ' : 'INN',
					target: pOptions.Users.length === 1 ? pOptions.Users[0] : pOptions.Users,
				});
			}

			if (pOptions?.StartDate && pOptions.StartDate.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'DocumentDate',
					comparator: 'GE',
					target: pOptions.StartDate,
				});
			}

			if (pOptions?.EndDate && pOptions.EndDate.length > 0)
			{
				tmpSearchQuery.filters.push({
					field: 'DocumentDate',
					comparator: 'LE',
					target: pOptions.EndDate,
				});
			}

			// Perform the search by calling both getDocumentSearchCount and getDocumentSearchResults
			Promise.all([
				this.getDocumentSearchCount(tmpSearchQuery),
				this.getDocumentSearchResults(tmpSearchQuery),
			])
				.then(([ tmpCountResults, tmpResults ]) =>
				{
					resolve({ results: tmpResults, count: tmpCountResults });
				})
				.catch((error) =>
				{
					this.log.error('Error during Document search:', error);
					reject(error);
				});
		});
	}

	/**
	 * This method is used to get the document types for a specific Customer.
	 * @param {number} pIDCustomer - The Customer id to get the document types for
	 * @param {Boolean} [excludeContainers] - Exclude "container" type forms from the list. Defaults to false.
	 *  @param {Boolean} [excludeNonManual] - Exclude forms that are not meant to be manually created. Defaults to false.
	 * @return {Promise<Array<any>>}
	 * @async
	 * @example
	 * getReportNamedInstancesByCustomer(1234);
	 */
	async getReportNamedInstancesByCustomer(pIDCustomer, excludeContainers = false, excludeNonManual = false)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getEntitySetWithPages('ReportNamedInstance', `FBV~IDCustomer~EQ~${ pIDCustomer }~FSF~Name~ASC~0`, ()=>{}, ()=>{}, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error('Failed to get Document Types list.', pError);
					return reject(pError);
				}
				if (excludeContainers)
				{
					const nonContainerForms = [];
					for (const f of pBody)
					{
						if (!f.Options?.Container)
						{
							nonContainerForms.push(f);
						}
					}
					pBody = nonContainerForms;
				}
				if (excludeNonManual)
				{
					const nonManualForms = [];
					for (const f of pBody)
					{
						if (!f.Options?.NoManualCreation)
						{
							nonManualForms.push(f);
						}
					}
					pBody = nonManualForms;
				}
				resolve(pBody.sort((a, b) => 
				{
					if ( a.Name < b.Name ){
						return -1;
					}
					if ( a.Name > b.Name ){
						return 1;
					}
				}));
			});
		});
	}

	async _getReportNamedInstancesByProjectPaged(pIDProject, page)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(`ReportNamedInstancesByProject/${ pIDProject }/${ page * 250 }/250`, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error(`Error performing search: ${pError}`, pError);
					reject(pError);
				}
				if (!Array.isArray(pBody))
				{
					return resolve([]);
				}
				resolve(pBody);
			});
		});
	}

	async getCreatableContainerRNIsByProject(pIDProject)
	{
		const results = await this.getReportNamedInstancesByProject(pIDProject);
		const containerForms = [];
		for (let r of results)
		{
			if (r.Options?.Container && !r.Options?.NoManualCreation)
			{
				containerForms.push(r);
			}
		}
		return containerForms;
	}

	async getReportNamedInstancesByProject(pIDProject, excludeContainers = false, excludeNonManual = false)
	{
		let hitEnd = false;
		let results = [];
		let page = 0;
		while (!hitEnd)
		{
			const pagedResults = await this._getReportNamedInstancesByProjectPaged(pIDProject, page);
			page += 1;
			if (pagedResults?.length < 250)
			{
				hitEnd = true;
			}
			results = results.concat(pagedResults);
		}
		if (excludeContainers)
		{
			const nonContainerForms = [];
			for (const f of results)
			{
				if (!f.Options?.Container)
				{
					nonContainerForms.push(f);
				}
			}
			results = nonContainerForms
		}
		if (excludeNonManual)
		{
			const nonManualForms = [];
			for (const f of results)
			{
				if (!f.Options?.NoManualCreation)
				{
					nonManualForms.push(f);
				}
			}
			results = nonManualForms;
		}
		return results.sort((a, b) => 
		{
			if ( a.Name < b.Name ){
				return -1;
			}
			if ( a.Name > b.Name ){
				return 1;
			}
		});
	}

	/**
	 * This method is used to handle the duplication of a document (a new instance by the clonee user, with all the previous form data carried over).
	 * @param {object} pCustomOptions - The custom options to use when duplicating the document
	 * @param {object} pDocumentObject - The document object to duplicate
	 * @param {Function} pCallback - The callback function to call when the duplication is complete
	 */
	async handleDocumentDuplication(pCustomOptions, pDocumentObject, pCallback)
	{
		if (!pDocumentObject || typeof pDocumentObject !== 'object')
		{
			this.pict.log.error('Document object was not passed.');
			return;
		}
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		// NOTE: This is super confusing, but a legacy thing that we need to work around
		// The document TYPE is stored in the TidingsData.Type field, but we need to find the matching document HASH to set the exact instance for the new document
		const documentRNIHash = pCustomOptions.Type || pDocumentObject?.ReportData?.DocumentType || pDocumentObject?.DocumentType;
		// get the RNIs for the customer
		const customerRNIs = await this.getReportNamedInstancesByCustomer(pDocumentObject?.ReportData?.IDCustomer || pDocumentObject?.IDCustomer);
		// find the matching document type via the project RNIs
		const match = customerRNIs.find((rni) => rni.Hash === documentRNIHash);
		if (!match)
		{
			this.pict.log.error('No matching RNI Hash has been set on this customer!');
			this.pict.providers.UploadTracking.untrackUpload(uploadID);
			return;
		}
		const documentName = pCustomOptions.Name || ( match?.Name || pDocumentObject?.ReportData?.Name || pDocumentObject?.Name ) + ' - Copy';
		// duplicate the document
		const newDocument = {
			IDDocument: 0,
			Version: 0,
			Form: pDocumentObject?.ReportData?.Form || pDocumentObject?.Form,
			Name: documentName,
			DocumentType: match.Hash,
			FormData: pDocumentObject?.ReportData?.FormData || pDocumentObject?.FormData || {},
			IDProject: pDocumentObject?.ReportData?.IDProject || pDocumentObject?.IDProject,
		};
		// if we have a custom document date, set it, otherwise use the current date
		if (pCustomOptions.DocumentDate)
		{
			newDocument.DocumentDate = pCustomOptions.DocumentDate;
		}
		// Stuff our meta data into the document (someday the server will provide this for us so we don't have to do this manually)
		newDocument.FormData.Meta = {
			ClonedFromDocumentType: pDocumentObject?.ReportData?.DocumentType || pDocumentObject?.DocumentType,
			ClonedFromDocumentId: pDocumentObject?.ReportData?.IDDocument || pDocumentObject?.IDDocument,
			ClonedFromGUIDDocument: pDocumentObject?.ReportData?.GUIDDocument || pDocumentObject?.GUIDDocument,
		};
		const tmpURL = 'Document';
		this.pict.HeadlightRestClient.postJSON(tmpURL, newDocument, (pError, pBody) =>
		{
			if (pError)
			{
				this.pict.log.error('Failed to duplicate document.', pError);
				this.pict.views.HeadlightPanels.toast({
					message: 'Failed to duplicate document!',
					type: 'is-danger',
				});
				this.pict.providers.UploadTracking.untrackUpload(uploadID);
			}
			else
			{
				this.pict.log.info('Document duplicated successfully.', pBody);
				if (typeof pCallback === 'function')
				{
					pCallback(pBody);
					this.pict.providers.UploadTracking.untrackUpload(uploadID);
				}
			}
		});
	}

	/**
	 * This method is used to handle the deletion of a document.
	 * @param {any} pIDDocument - The document id to delete
	 * @param {Function} pCallback - The callback function to call when the deletion is complete
	 * @return {void}
	 */
	handleDocumentDeletion(pIDDocument, pCallback)
	{
		if (!pIDDocument)
		{
			this.pict.log.error('IDDocument object was not passed.');
			return;
		}
		this.pict.HeadlightRestClient.deleteEntity('Document', pIDDocument, (pError, pBody) =>
		{
			if (pError)
			{
				this.pict.log.error('Failed to delete document.', pError);
				this.pict.views.HeadlightPanels.toast({
					message: 'Failed to delete document!',
					type: 'is-danger',
				});
				pCallback(pError);
			}
			else
			{
				this.pict.log.info('Document deleted successfully.', pBody);
				if (typeof pCallback === 'function')
				{
					pCallback(pBody);
				}
			}
		});
	}

	/**
	 * This method is used to handle the creation of a document.
	 * @param {object} pDocument - The document object to create
	 * @param {boolean} pMuteToasts - Whether to mute the toasts
	 * @return {Promise<any>}
	 * @async
	 */
	async createDocument(pDocument, pMuteToasts = false)
	{
		if (!pDocument || typeof pDocument !== 'object')
		{
			this.pict.log.error('Document object was not passed.');
			return;
		}
		// check for required fields
		if (!pDocument?.Name || !pDocument?.DocumentType || !pDocument?.IDProject)
		{
			this.pict.log.error('Document object is missing required fields.');
			return;
		}
		const uploadID = this.pict.providers.UploadTracking.trackUpload();
		// create a new document object to send to the server
		const newDocument = {
			IDDocument: 0,
			Version: 0,
			Form: pDocument?.Form,
			Name: pDocument?.Name,
			DocumentType: pDocument?.DocumentType,
			IDProject: pDocument?.IDProject,
		};
		if (pDocument.GUIDDocument)
		{
			newDocument.GUIDDocument = pDocument.GUIDDocument;
		}
		// optional fields
		if (pDocument?.DocumentDate)
		{
			newDocument.DocumentDate = pDocument.DocumentDate;
		}
		if (pDocument?.FormData)
		{
			newDocument.FormData = pDocument.FormData;
		}
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.postJSON('Document', pDocument, (pError, pBody) =>
			{
				if (pError)
				{
					this.pict.log.error('Failed to create document.', pError);
					if (!pMuteToasts)
					{
						this.pict.views.HeadlightPanels.toast({
							message: 'Failed to create document!',
							type: 'is-danger',
						});
					}
					this.pict.providers.UploadTracking.untrackUpload(uploadID);
					return reject(pError);
				}
				if (!pMuteToasts)
				{
					this.pict.views.HeadlightPanels.toast({
						message: 'Document created successfully!',
						type: 'is-success',
					});
				}
				this.pict.providers.UploadTracking.untrackUpload(uploadID);
				resolve(pBody);
			});
		});
	}

	setScreenResizeIcon(screenMode)
	{
		const iconElement = document.getElementById('HeadlightPanels-NavBar-FullScreen-Button-Icon');
		const maximizeClass = 'fa-up-right-and-down-left-from-center';
		const minimizeClass = 'fa-down-left-and-up-right-to-center';
		if (iconElement)
		{
			if (screenMode === 'widescreen')
			{
				iconElement.classList.remove(maximizeClass);
				iconElement.classList.add(minimizeClass);
			}
			else
			{
				iconElement.classList.remove(minimizeClass);
				iconElement.classList.add(maximizeClass);
			}
		}
	}

	handleScreenResize(passedID)
	{
		let screenMode = 'standard';
		const alwaysWideScreenModeClass = 'always-widescreen-mode';
		const docElement = passedID ? document.getElementById(passedID) : document.body;
		if (docElement.classList.contains(alwaysWideScreenModeClass))
		{
			screenMode = 'standard';
			docElement.classList.remove(alwaysWideScreenModeClass);
		}
		else
		{
			screenMode = 'widescreen';
			docElement.classList.add(alwaysWideScreenModeClass);
		}
		this.pict.views.HeadlightPaneSettings.updateUserPreferences('ScreenWidth', screenMode);
		this.setScreenResizeIcon(screenMode);
	}

	/**
	 * This method is used to check if an observation is included in the document.
	 * @param {string} pObservationID - The observation ID to check
	 * @return {boolean} - True if the observation is included in the document, false otherwise
	 * @example
	 * isObservationIncludedInDocument('1234');
	 */
	isObservationIncludedInDocument(pObservationID)
	{
		if (!pObservationID)
		{
			this.pict.log.error('No Observation ID provided.');
			return false;
		}
		const tmpObservation = this.AppData?.DocumentData?.ReportData?.Observations?.find((observation) => observation.IDObservation === pObservationID);
		if (tmpObservation)
		{
			return true;
		}
		return false;
	}
}

module.exports = DocumentProvider;
module.exports.default_configuration = { AutoSaveDataWithApp: true, SaveWatchdogTimeoutMS: 120000 };
